import React, {useEffect, useMemo, useRef} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Text} from '@forecast-it/design-system';
import {NovaAgentDraft} from '../state/useNovaAgentDraft';
import {PromptTarget} from '../state/useNovaPromptState';
import {NovaVariable, buildIsTokenPermitted} from '../shared/novaVariableLibrary';
import {validateInput} from '../shared/novaInputValidation';
import {MonospacePromptEditor} from './MonospacePromptEditor';
import {NovaVariableLibrary} from './NovaVariableLibrary';
import {NovaInputIssues} from './NovaInputIssues';

// One rhythm for every section on the tab (design system spacing step 800). The sections are
// self-contained blocks — several carry their own border or dashed outline — so at the step below this
// they crowd each other and stop reading as separate things. Kept identical on both tabs so switching
// between them doesn't shift the layout.
const Container = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	min-height: 0;
	gap: 32px;
`;

// Tall enough to work with a multi-section agent.md without immediately needing to scroll.
const EDITOR_HEIGHT = 480;

interface Props {
	draft: NovaAgentDraft;
	// The variables the library offers for insertion.
	variables: NovaVariable[];
	// The removed variables, needed only to tell a reference to one from a name that never existed.
	removed: NovaVariable[];
}

// The agent.md editor: the agent's optional standing context, applied on every run so the author
// does not restate it in the per-run template. Bound to the same working draft as the Prompt tab, so
// edits here survive a tab switch and are committed by the header Save along with everything else.
//
// The variable library sits above the editor and inserts at the draft's cursor. Only static,
// non-customer-derived variables are insertable here; the library greys out the rest and says where
// they are allowed instead. A customer-data reference typed in by hand is not blocked — it is
// flagged below the editor, along with references that no longer resolve and broken Markdown.
//
// Nothing on this tab is required. An empty agent.md is the normal case and reports nothing at all.
export function NovaAgentMdTab({draft, variables, removed}: Props): JSX.Element {
	const {formatMessage} = useIntl();
	const {values, setValue, setActiveTarget, activeTarget, insertTokenAtCursor} = draft.promptState;

	// Captured once, at mount, rather than read as a plain dependency — see the equivalent guard in
	// NovaPromptTab for why a mount-only check is what's wanted here, not one that re-runs whenever
	// the target changes.
	const arrivingTargetRef = useRef(activeTarget);

	// Arriving here from the Prompt tab makes this the insertion target, so a variable clicked in
	// the library before the editor is ever focused lands here rather than in the Prompt tab's last
	// active input. This tab owns only one target, so the only time it is already active is a
	// remount that isn't a genuine arrival from elsewhere (e.g. the Deploy tab in between); in that
	// case there is nothing to claim, so nothing is reset. Claiming also resets the insertion point
	// to end-of-input, since the editor remounts unfocused either way and any caret from an earlier
	// visit is no longer visible to the author.
	useEffect(() => {
		if (arrivingTargetRef.current === PromptTarget.System || arrivingTargetRef.current === PromptTarget.User) {
			setActiveTarget(PromptTarget.AgentMd);
		}
	}, [setActiveTarget]);

	const isPermitted = useMemo(() => buildIsTokenPermitted(variables), [variables]);
	const issues = useMemo(
		() => validateInput(values.agentMd, PromptTarget.AgentMd, {offered: variables, removed}),
		[values.agentMd, variables, removed]
	);

	return (
		<Container data-cy="nova-agent-md-tab" data-testid="nova-agent-md-tab">
			<Text color="auxiliary" data-cy="nova-agent-md-explainer">
				{formatMessage({id: 'nova.agentMd.explainer'})}
			</Text>
			<NovaVariableLibrary
				variables={variables}
				activeTarget={activeTarget}
				isPermitted={isPermitted}
				onInsert={token => insertTokenAtCursor(token)}
			/>
			<MonospacePromptEditor
				label={formatMessage({id: 'nova.agentMd.label'})}
				dataCy="nova-agent-md"
				target={PromptTarget.AgentMd}
				value={values.agentMd}
				height={EDITOR_HEIGHT}
				isOptional
				helperText={formatMessage({id: 'nova.agentMd.helperText'})}
				onChange={(value: string) => setValue(PromptTarget.AgentMd, value)}
				onActivate={setActiveTarget}
				caption={
					activeTarget === PromptTarget.AgentMd ? formatMessage({id: 'nova.prompt.insertTargetHint'}) : undefined
				}
				issues={issues.length > 0 ? <NovaInputIssues issues={issues} dataCy="nova-agent-md-issues" /> : undefined}
			/>
		</Container>
	);
}
